import { useLocation, useNavigate } from "react-router";
import { ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { useAuth } from "@/hooks/useAuth";
import { puedeAcceder } from "@/lib/permisos";
import type { ReactNode } from "react";

/* ---------------------------------------------------------------------------
   Guarda de ruta por perfil. Si el perfil del usuario no puede entrar a la
   ruta actual, muestra un estado de "sin permiso" en vez de redirigir en
   silencio: que la pantalla desaparezca sin explicación se lee como un error
   de la app, no como una decisión.

   No es la autorización: esa vive en la API, que responde 403 igual. Acá se
   evita la pantalla rota que sería pedir datos que el servidor va a negar.
--------------------------------------------------------------------------- */

export function RequiereRol({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perfil = user?.perfil ?? null;

  if (puedeAcceder(perfil, pathname)) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center p-8">
      <Empty className="rounded-xl border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ShieldOff />
          </EmptyMedia>
          <EmptyTitle>Esta sección es del Super Admin</EmptyTitle>
          <EmptyDescription>
            Entraste como <strong>{user?.name}</strong> ({perfil?.nombre}). La
            administración de la plataforma —usuarios y configuración— solo la maneja el
            Super Admin.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {/* A la primera pantalla del usuario, que es la cola de informes:
              la portada de accesos —el "inicio" al que volvía este botón— se
              eliminó (ver `App.tsx`). */}
          <Button variant="outline" onClick={() => navigate("/informes")}>
            Volver a Informes en proceso
          </Button>
        </EmptyContent>
      </Empty>
    </div>
  );
}
